import { useCallback, useEffect, useRef, useState } from 'react';
import userService from '../services/userService';
import {
  EMPTY_USER_FOLLOW_STATUS,
  normalizeUserFollowStatus,
} from '../utils/userFollowStatus';

const DEFAULT_LOG_LABEL = 'user follow status';

export default function useUserFollowStatus(userId, {
  enabled = true,
  logLabel = DEFAULT_LOG_LABEL,
} = {}) {
  const [followStatus, setFollowStatus] = useState(EMPTY_USER_FOLLOW_STATUS);
  const requestRef = useRef(0);

  const applyFollowStatus = useCallback((status) => {
    requestRef.current += 1;
    setFollowStatus(normalizeUserFollowStatus(status));
  }, []);

  useEffect(() => {
    requestRef.current += 1;
    const requestId = requestRef.current;
    setFollowStatus(EMPTY_USER_FOLLOW_STATUS);

    if (!enabled || !userId) {
      return undefined;
    }

    const controller = new AbortController();
    userService.getUserFollowStatus(userId, { signal: controller.signal })
      .then((status) => {
        if (requestId === requestRef.current) {
          setFollowStatus(normalizeUserFollowStatus(status));
        }
      })
      .catch((error) => {
        if (controller.signal.aborted || requestId !== requestRef.current) {
          return;
        }
        console.warn(`Failed to load ${logLabel}.`, error);
        setFollowStatus(EMPTY_USER_FOLLOW_STATUS);
      });

    return () => {
      controller.abort();
    };
  }, [enabled, logLabel, userId]);

  return {
    followStatus,
    applyFollowStatus,
  };
}
